import React, { useState, useEffect } from 'react';
import { RiExchangeDollarFill } from "react-icons/ri";
import { BsCartX } from "react-icons/bs";
import { GrCube } from "react-icons/gr";
import { MdOutlineTrain } from "react-icons/md";
import { LuClipboardX } from "react-icons/lu";
import { LuClock } from "react-icons/lu";
import { LuClipboardCheck } from "react-icons/lu";
import { RiInbox2Line } from "react-icons/ri";
import { Link, useParams } from 'react-router-dom';
import InvoiceDetailPopup from '../../Invoice/InvoiceDetail';
import axios from 'axios';

export const SellerOrder = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortType, setSortType] = useState("ALL");
  const [sortedOrders, setSortedOrders] = useState([]);
  const itemsPerPage = 10;

  // Date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterType, setDateFilterType] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [dateSortOrder, setDateSortOrder] = useState('desc'); // 'asc' or 'desc'

  const [stats, setStats] = useState({
    totalReceived: 0,
    cancelled: 0,
    shipped: 0,
    delivered: 0,
    repeat: 0,
    pendingPayment: 0,
    returned: 0,
    inProgress: 0
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [orders, sortType, startDate, endDate, dateFilterType, dateSortOrder]);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      const seller_id = localStorage.getItem("seller_id");
      console.log("Seller ID:", seller_id);

      if (!seller_id) {
        setError("Seller not logged in");
        return;
      }

      const response = await fetch(
        "https://cyberathon.com/multivendor/index.php/Api/Master_controller/list_new_order",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            seller_id: seller_id
          })
        }
      );

      console.log("API Response Status:", response.status);

      const result = await response.json();
      console.log("Seller Orders:", result);

      if (result.status === true) {
        setOrders(result.data);
      } else {
        setOrders([]);
        setError(result.message || "No orders found");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (ordersData) => {
    const safeLower = (value) =>
      typeof value === "string" ? value.toLowerCase() : "";

    const stats = {
      totalReceived: ordersData.length,

      cancelled: ordersData.filter(
        o => ["cancelled", "canceled"].includes(safeLower(o.order_status))
      ).length,

      shipped: ordersData.filter(
        o => safeLower(o.order_status) === "shipped"
      ).length,

      delivered: ordersData.filter(
        o => ["delivered", "completed"].includes(safeLower(o.order_status))
      ).length,

      repeat: 0,

      pendingPayment: ordersData.filter(
        o => safeLower(o.payment_status) === "pending"
      ).length,

      returned: ordersData.filter(
        o => safeLower(o.order_status) === "returned"
      ).length,

      inProgress: ordersData.filter(
        o => ["processing", "packaging"].includes(safeLower(o.order_status))
      ).length
    };

    setStats(stats);
  };

  const filterByDateRange = (ordersData) => {
    if (dateFilterType === 'all') {
      return ordersData;
    }

    const now = new Date();
    let start, end;

    switch (dateFilterType) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
        break;
      
      case 'week':
        start = new Date(now.setDate(now.getDate() - 7));
        end = new Date();
        break;
      
      case 'month':
        start = new Date(now.setMonth(now.getMonth() - 1));
        end = new Date();
        break;
      
      case 'custom':
        if (!startDate || !endDate) return ordersData;
        start = new Date(startDate);
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        break;
      
      default:
        return ordersData;
    }

    return ordersData.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= start && orderDate <= end;
    });
  };

  const sortByDate = (ordersData) => {
    return [...ordersData].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      if (dateSortOrder === 'asc') {
        return dateA - dateB; // Oldest first
      } else {
        return dateB - dateA; // Newest first
      }
    });
  };

  const applyFilters = () => {
    let filtered = [...orders];

    const safeLower = (value) =>
      typeof value === "string" ? value.toLowerCase() : "";

    // Apply status filter
    switch (sortType) {
      case "CANCELLED":
        filtered = filtered.filter(o => 
          ["cancelled", "canceled"].includes(safeLower(o.order_status))
        );
        break;
      case "SHIPPED":
        filtered = filtered.filter(o => safeLower(o.order_status) === "shipped");
        break;
      case "DELIVERED":
        filtered = filtered.filter(o => 
          ["delivered", "completed"].includes(safeLower(o.order_status))
        );
        break;
      case "PENDING_PAYMENT":
        filtered = filtered.filter(o => safeLower(o.payment_status) === "pending");
        break;
      case "RETURNED":
        filtered = filtered.filter(o => safeLower(o.order_status) === "returned");
        break;
      case "IN_PROGRESS":
        filtered = filtered.filter(o =>
          ["processing", "packaging"].includes(safeLower(o.order_status))
        );
        break;
      default:
        break;
    }

    // Apply date filter
    filtered = filterByDateRange(filtered);

    // Apply date sorting
    filtered = sortByDate(filtered);

    setSortedOrders(filtered);
    calculateStats(filtered);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (type) => {
    setDateFilterType(type);
    if (type !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleCustomDateFilter = () => {
    if (startDate && endDate) {
      setDateFilterType('custom');
    }
  };

  const clearDateFilter = () => {
    setDateFilterType('all');
    setStartDate('');
    setEndDate('');
  };

  const toggleDateSort = () => {
    setDateSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleSort = (type) => {
    setSortType(type);
  };

  const getOrderStatusBadge = (status) => {
    const statusLower =
      typeof status === "string" ? status.toLowerCase() : "pending";

    if (statusLower === 'completed' || statusLower === 'delivered') {
      return 'badge border border-success text-success px-2 py-1 fs-13';
    } 
    if (statusLower === 'pending') {
      return 'badge border border-secondary text-secondary px-2 py-1 fs-13';
    }
    if (statusLower === 'packaging' || statusLower === 'processing') {
      return 'badge border border-warning text-warning px-2 py-1 fs-13';
    }
    if (statusLower === 'cancelled' || statusLower === 'canceled') {
      return 'badge border border-danger text-danger px-2 py-1 fs-13';
    }
    return 'badge border border-info text-info px-2 py-1 fs-13';
  };

  const getPaymentStatusBadge = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'paid' || statusLower === 'completed') {
      return 'badge bg-success text-light px-2 py-1 fs-13';
    } else if (statusLower === 'pending') {
      return 'badge bg-light text-dark px-2 py-1 fs-13';
    } else if (statusLower === 'refund') {
      return 'badge bg-light text-dark px-2 py-1 fs-13';
    }
    return 'badge bg-secondary text-light px-2 py-1 fs-13';
  };

  const getPageNumbers = () => {
    const pages = [];

    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, currentPage + 1);

    if (currentPage === 1) {
      endPage = Math.min(3, totalPages);
    }

    if (currentPage === totalPages) {
      startPage = Math.max(totalPages - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentOrders = sortedOrders.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Handle Invoice Modal
  const handleShowInvoice = (orderId) => {
    setSelectedOrderId(orderId);
    setShowInvoiceModal(true);
    console.log("Order ID fro invoice:", orderId);
  };

  const handleCloseInvoice = () => {
    setShowInvoiceModal(false);
    setSelectedOrderId(null);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box">
              <h4 className="mb-0 fw-semibold">My Orders</h4>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="javascript: void(0);">Orders</a>
                </li>
                <li className="breadcrumb-item active">Orders List</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Date Filter Section */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label">Quick Date Filter</label>
                    <div className="btn-group w-100" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('all')}
                      >
                        All
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'today' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('today')}
                      >
                        Today
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('week')}
                      >
                        Last 7 Days
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dateFilterType === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleDateFilterChange('month')}
                      >
                        Last 30 Days
                      </button>
                    </div>
                  </div>
                  
                  <div className="col-md-2">
                    <label className="form-label">Sort by Date</label>
                    <button
                      className="btn btn-outline-secondary w-100"
                      onClick={toggleDateSort}
                    >
                      <i className={`fa-solid fa-arrow-${dateSortOrder === 'desc' ? 'down' : 'up'} me-2`}></i>
                      {dateSortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                    </button>
                  </div>
                  
                  <div className="col-md-2">
                    <label htmlFor="startDate" className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="col-md-2">
                    <label htmlFor="endDate" className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <button
                      className="btn btn-primary me-2"
                      onClick={handleCustomDateFilter}
                      disabled={!startDate || !endDate}
                    >
                      <i className="fa-solid fa-filter me-1"></i>
                      Apply Custom Range
                    </button>
                    {(dateFilterType !== 'all' || startDate || endDate) && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={clearDateFilter}
                      >
                        <i className="fa-solid fa-times me-1"></i>
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                
                {dateFilterType !== 'all' && (
                  <div className="mt-2">
                    <small className="text-muted">
                      <i className="fa-solid fa-info-circle me-1"></i>
                      Showing orders from: <strong>
                        {dateFilterType === 'today' && 'Today'}
                        {dateFilterType === 'week' && 'Last 7 days'}
                        {dateFilterType === 'month' && 'Last 30 days'}
                        {dateFilterType === 'custom' && startDate && endDate && 
                          `${formatDate(startDate)} to ${formatDate(endDate)}`}
                      </strong>
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row">
          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Total Order Received</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.totalReceived}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <RiExchangeDollarFill className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Order Cancel</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.cancelled}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <BsCartX className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Order Shipped</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.shipped}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <GrCube className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Order Delivered</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.delivered}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <MdOutlineTrain className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Repeat Order</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.repeat}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <LuClipboardX className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Pending Payment</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.pendingPayment}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <LuClock className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">Return Orders</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.returned}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <LuClipboardCheck className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-xl-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="card-title mb-2">In Progress</h4>
                    <p className="text-muted fw-medium fs-22 mb-0">{stats.inProgress}</p>
                  </div>
                  <div>
                    <div className="avatar-md bg-primary bg-opacity-10 rounded">
                      <RiInbox2Line className="fs-32 text-primary avatar-title" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="row">
          <div className="col-xl-12">
            <div className="card">
              <div className="d-flex card-header justify-content-between align-items-center">
                <div>
                  <h4 className="card-title">All Order List</h4>
                </div>
                <div className="dropdown">
                  <a
                    href="#"
                    className="dropdown-toggle btn btn-sm btn-outline-light rounded"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Sort Orders
                  </a>
                  <div className="dropdown-menu dropdown-menu-end">
                    <button className="dropdown-item" onClick={() => handleSort("ALL")}>Total Order Received</button>
                    <button className="dropdown-item" onClick={() => handleSort("CANCELLED")}>Order Cancel</button>
                    <button className="dropdown-item" onClick={() => handleSort("SHIPPED")}>Order Shipped</button>
                    <button className="dropdown-item" onClick={() => handleSort("DELIVERED")}>Order Delivered</button>
                    <button className="dropdown-item" onClick={() => handleSort("PENDING_PAYMENT")}>Pending Payment</button>
                    <button className="dropdown-item" onClick={() => handleSort("RETURNED")}>Return Orders</button>
                    <button className="dropdown-item" onClick={() => handleSort("IN_PROGRESS")}>In Progress</button>
                  </div>
                </div>
                <button className="btn btn-sm btn-primary">Generate Invoice</button>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger m-3" role="alert">
                    {error}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 table-hover table-centered">
                      <thead className="bg-light-subtle">
                        <tr>
                          <th>Order ID</th>
                          <th>Created at</th>
                          <th>Customer</th>
                          <th>Product</th>
                          <th>Total</th>
                          <th>Payment Status</th>
                          <th>Payment Mode</th>
                          <th>Items</th>
                          <th>Order Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.length > 0 ? (
                          currentOrders.map((order) => (
                            <tr key={order.order_id}>
                              <td>#{order.order_id}</td>
                              <td>{formatDate(order.created_at)}</td>
                              <td>
                                <a href="#!" className="link-primary fw-medium">
                                  {order.customer_name}
                                </a>
                              </td>
                              <td>{order.items?.[0]?.product_name || "-"}</td>
                              <td>₹{parseFloat(order.total).toFixed(2)}</td>
                              <td>
                                <span className={getPaymentStatusBadge(order.payment_status)}>
                                  {order.payment_status}
                                </span>
                              </td>
                              <td>{order.payment_mode}</td>
                              <td>{order.items?.[0]?.qty || "-"}</td>
                              <td>
                                <span className={getOrderStatusBadge(order.order_status)}>
                                  {order.order_status}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => handleShowInvoice(order.order_id)}
                                    className="btn btn-soft-success btn-sm"
                                    title="View Invoice"
                                  >
                                    <i className="fa-solid fa-file-invoice align-middle fs-18"></i>
                                  </button>
                                  <Link
                                    to={`/seller-order-view/${order.order_id}`}
                                    className="btn btn-light btn-sm"
                                  >
                                    <i className="fa-solid fa-eye fs-18"></i>
                                  </Link>
                                  <Link to={`/order-list-edit/${order.order_id}`} className="btn btn-soft-primary btn-sm">
                                    <i className="fa-solid fa-pencil align-middle fs-18"></i>
                                  </Link>
                                  <Link className="btn btn-soft-danger btn-sm">
                                    <i className="fa-solid fa-trash align-middle fs-18"></i>
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center py-4">
                              No orders found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="card-footer border-top">
                <nav aria-label="Page navigation example">
                  <ul className="pagination justify-content-end mb-0">
                    {/* Previous */}
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={handlePrev}>
                        Previous
                      </button>
                    </li>

                    {/* Page Numbers */}
                    {getPageNumbers().map((page) => (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    {/* Next */}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button className="page-link" onClick={handleNext}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Detail Popup */}
      <InvoiceDetailPopup
        show={showInvoiceModal}
        onClose={handleCloseInvoice}
        orderId={selectedOrderId}
      />

      <footer className="footer">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 text-center">
              © Venton. Crafted by{" "}
              <iconify-icon
                icon="iconamoon:heart-duotone"
                className="fs-18 align-middle text-danger"
              />{" "}
              <a href="" className="fw-bold footer-text" target="_blank">
                Techzaa
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};


export const OrderView = () => {
  const { id } = useParams(); // Get order ID from URL parameter (/order-detail/3)
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      fetchOrderDetails(id);
    }
  }, [id]);

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      setError(null);
      
      // POST request with order_id in body
      const response = await axios.post(
        'https://cyberathon.com/multivendor/index.php/Api/Master_controller/list_new_order',
        { order_id: orderId },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.status && response.data.data.length > 0) {
        setOrderData(response.data.data[0]);
      } else {
        setError('No order data found');
      }
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatCurrency = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getPaymentStatusBadge = (status) => {
    if (status === 'Pending') {
      return 'badge bg-warning-subtle text-warning px-2 py-1 fs-13';
    }
    return 'badge bg-success-subtle text-success px-2 py-1 fs-13';
  };

  const getOrderStatusBadge = (status) => {
    const statusMap = {
      'Pending': 'border border-warning text-warning',
      'Processing': 'border border-info text-info',
      'Shipped': 'border border-primary text-primary',
      'Delivered': 'border border-success text-success',
      'Cancelled': 'border border-danger text-danger'
    };
    return `${statusMap[status] || 'border border-secondary text-secondary'} fs-13 px-2 py-1 rounded`;
  };

  const getProgressWidth = (status) => {
    const progressMap = {
      'Pending': [100, 0, 0, 0, 0],
      'Processing': [100, 100, 60, 0, 0],
      'Shipped': [100, 100, 100, 60, 0],
      'Delivered': [100, 100, 100, 100, 100]
    };
    return progressMap[status] || [0, 0, 0, 0, 0];
  };

  const getProgressColor = (index, status) => {
    const progressWidths = getProgressWidth(status);
    if (progressWidths[index] === 100) return 'bg-success';
    if (progressWidths[index] > 0) return 'bg-warning';
    return 'bg-primary';
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const calculateTax = (subtotal, taxRate = 15.5) => {
    return (subtotal * taxRate) / 100;
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            {error || 'No order data available'}
          </div>
        </div>
      </div>
    );
  }

  const { items, total, order_status, payment_status, payment_mode, order_date, created_at, 
          order_number, customer_name, phone, contact_address } = orderData;
  
  const subtotal = calculateSubtotal(items);
  const discount = 0;
  const deliveryCharge = 0;
  const tax = calculateTax(subtotal);
  const totalAmount = subtotal - discount + deliveryCharge + tax;
  const progressWidths = getProgressWidth(order_status);

  return (
    <div className="page-content">
      {/* Start Container */}
      <div className="container-fluid">
        {/* ========== Page Title Start ========== */}
        <div className="row">
          <div className="col-12">
            <div className="page-title-box">
              <h4 className="mb-0 fw-semibold">Order Details</h4>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <a href="javascript: void(0);">Order</a>
                </li>
                <li className="breadcrumb-item active">Order Details</li>
              </ol>
            </div>
          </div>
        </div>
        {/* ========== Page Title End ========== */}
        <div className="row">
          <div className="col-xl-9 col-lg-8">
            <div className="row">
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div>
                        <h4 className="fw-medium text-dark d-flex align-items-center gap-2">
                          {order_number}{" "}
                          <span className={getPaymentStatusBadge(payment_status)}>
                            {payment_status}
                          </span>
                          <span className={getOrderStatusBadge(order_status)}>
                            {order_status}
                          </span>
                        </h4>
                        <p className="mb-0">
                          Order / Order Details / {order_number} - {formatDate(created_at)} at{" "}
                          {formatTime(created_at)}
                        </p>
                      </div>
                      <div>
                        <a href="#!" className="btn btn-outline-secondary">
                          Refund
                        </a>
                        <a href="#!" className="btn btn-outline-secondary">
                          Return
                        </a>
                        <a href="#!" className="btn btn-primary">
                          Edit Order
                        </a>
                      </div>
                    </div>
                    <div className="mt-4">
                      <h4 className="fw-medium text-dark">Progress</h4>
                    </div>
                    <div className="row row-cols-xxl-5 row-cols-md-2 row-cols-1">
                      <div className="col">
                        <div className="progress mt-3" style={{ height: 10 }}>
                          <div
                            className={`progress-bar progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(0, order_status)}`}
                            role="progressbar"
                            style={{ width: `${progressWidths[0]}%` }}
                            aria-valuenow={progressWidths[0]}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                        <p className="mb-0 mt-2">Order Confirming</p>
                      </div>
                      <div className="col">
                        <div className="progress mt-3" style={{ height: 10 }}>
                          <div
                            className={`progress-bar progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(1, order_status)}`}
                            role="progressbar"
                            style={{ width: `${progressWidths[1]}%` }}
                            aria-valuenow={progressWidths[1]}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                        <p className="mb-0 mt-2">Payment {payment_status}</p>
                      </div>
                      <div className="col">
                        <div className="progress mt-3" style={{ height: 10 }}>
                          <div
                            className={`progress-bar progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(2, order_status)}`}
                            role="progressbar"
                            style={{ width: `${progressWidths[2]}%` }}
                            aria-valuenow={progressWidths[2]}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <p className="mb-0">Processing</p>
                          {order_status === 'Processing' && (
                            <div
                              className="spinner-border spinner-border-sm text-warning"
                              role="status"
                            >
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col">
                        <div className="progress mt-3" style={{ height: 10 }}>
                          <div
                            className={`progress-bar progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(3, order_status)}`}
                            role="progressbar"
                            style={{ width: `${progressWidths[3]}%` }}
                            aria-valuenow={progressWidths[3]}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                        <p className="mb-0 mt-2">Shipping</p>
                      </div>
                      <div className="col">
                        <div className="progress mt-3" style={{ height: 10 }}>
                          <div
                            className={`progress-bar progress-bar progress-bar-striped progress-bar-animated ${getProgressColor(4, order_status)}`}
                            role="progressbar"
                            style={{ width: `${progressWidths[4]}%` }}
                            aria-valuenow={progressWidths[4]}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                        <p className="mb-0 mt-2">Delivered</p>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex flex-wrap align-items-center justify-content-between bg-light-subtle gap-2">
                    <p className="border rounded mb-0 px-2 py-1 bg-body">
                      <i className="bx bx-arrow-from-right align-middle fs-16" />
                      Estimated shipping date :{" "}
                      <span className="text-dark fw-medium">
                        {formatDate(new Date(new Date(order_date).getTime() + 2 * 24 * 60 * 60 * 1000))}
                      </span>
                    </p>
                    <div>
                      <a href="#!" className="btn btn-primary">
                        Make As Ready To Ship
                      </a>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">Product</h4>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table align-middle mb-0 table-hover table-centered">
                        <thead className="bg-light-subtle border-bottom">
                          <tr>
                            <th>Product Name &amp; Size</th>
                            <th>Status</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Tax</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="rounded bg-light avatar-md d-flex align-items-center justify-content-center">
                                    <img
                                      src={`https://cyberathon.com/multivendor/${item.multi_image}`}
                                      alt={item.product_name}
                                      className="avatar-md"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/images/product/placeholder.png';
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <a
                                      href="#!"
                                      className="text-dark fw-medium fs-15"
                                    >
                                      {item.product_name}
                                    </a>
                                    <p className="text-muted mb-0 mt-1 fs-13">
                                      <span>Size: </span>{item.size}
                                      <span className="ms-2">Color: </span>{item.color}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-success-subtle text-success px-2 py-1 fs-13">
                                  Ready
                                </span>
                              </td>
                              <td>{item.qty}</td>
                              <td>{formatCurrency(item.price)}</td>
                              <td>{formatCurrency(0)}</td>
                              <td>{formatCurrency(item.subtotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title">Order Timeline</h4>
                  </div>
                  <div className="card-body">
                    <div className="position-relative ms-2">
                      <span className="position-absolute start-0 top-0 border border-dashed h-100" />
                      
                      {order_status === 'Processing' && (
                        <div className="position-relative ps-4">
                          <div className="mb-4">
                            <span className="position-absolute start-0 avatar-sm translate-middle-x bg-light d-inline-flex align-items-center justify-content-center rounded-circle">
                              <div
                                className="spinner-border spinner-border-sm text-warning"
                                role="status"
                              >
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </span>
                            <div className="ms-2 d-flex flex-wrap gap-2 align-items-center justify-content-between">
                              <div>
                                <h5 className="mb-1 text-dark fw-medium fs-15">
                                  The packing has been started
                                </h5>
                                <p className="mb-0">Confirmed by {customer_name}</p>
                              </div>
                              <p className="mb-0">{formatDate(created_at)}, {formatTime(created_at)}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="position-relative ps-4">
                        <div className="mb-4">
                          <span className="position-absolute start-0 avatar-sm translate-middle-x bg-light d-inline-flex align-items-center justify-content-center rounded-circle text-success fs-20">
                            <i className="bx bx-check-circle" />
                          </span>
                          <div className="ms-2 d-flex flex-wrap gap-2 align-items-center justify-content-between">
                            <div>
                              <h5 className="mb-1 text-dark fw-medium fs-15">
                                Order Payment
                              </h5>
                              <p className="mb-2">Using {payment_mode}</p>
                              <div className="d-flex align-items-center gap-2">
                                <p className="mb-1 text-dark fw-medium">Status :</p>
                                <span className={getPaymentStatusBadge(payment_status)}>
                                  {payment_status}
                                </span>
                              </div>
                            </div>
                            <p className="mb-0">{formatDate(created_at)}, {formatTime(created_at)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="position-relative ps-4">
                        <div className="mb-2">
                          <span className="position-absolute start-0 avatar-sm translate-middle-x bg-light d-inline-flex align-items-center justify-content-center rounded-circle text-success fs-20">
                            <i className="bx bx-check-circle" />
                          </span>
                          <div className="ms-2 d-flex flex-wrap gap-2 align-items-center justify-content-between">
                            <div>
                              <h5 className="mb-2 text-dark fw-medium fs-15">
                                {items.length} Order confirmed by {customer_name}
                              </h5>
                              {items.map((item, index) => (
                                <a
                                  key={index}
                                  href="#!"
                                  className="badge bg-light text-dark fw-normal px-2 py-1 fs-13 me-1"
                                >
                                  {item.product_name}
                                </a>
                              ))}
                            </div>
                            <p className="mb-0">{formatDate(created_at)}, {formatTime(created_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card bg-light-subtle">
                  <div className="card-body">
                    <div className="row g-3 g-lg-0">
                      <div className="col-lg-3 border-end">
                        <div className="d-flex align-items-center gap-3 justify-content-between px-3">
                          <div>
                            <p className="text-dark fw-medium fs-16 mb-1">Order Number</p>
                            <p className="mb-0">{order_number}</p>
                          </div>
                          <div className="avatar bg-light d-flex align-items-center justify-content-center rounded">
                            <iconify-icon
                              icon="solar:shop-2-bold-duotone"
                              className="fs-35 text-primary"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 border-end">
                        <div className="d-flex align-items-center gap-3 justify-content-between px-3">
                          <div>
                            <p className="text-dark fw-medium fs-16 mb-1">Date</p>
                            <p className="mb-0">{formatDate(order_date)}</p>
                          </div>
                          <div className="avatar bg-light d-flex align-items-center justify-content-center rounded">
                            <iconify-icon
                              icon="solar:calendar-date-bold-duotone"
                              className="fs-35 text-primary"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 border-end">
                        <div className="d-flex align-items-center gap-3 justify-content-between px-3">
                          <div>
                            <p className="text-dark fw-medium fs-16 mb-1">
                              Paid By
                            </p>
                            <p className="mb-0">{customer_name}</p>
                          </div>
                          <div className="avatar bg-light d-flex align-items-center justify-content-center rounded">
                            <iconify-icon
                              icon="solar:user-circle-bold-duotone"
                              className="fs-35 text-primary"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3">
                        <div className="d-flex align-items-center gap-3 justify-content-between px-3">
                          <div>
                            <p className="text-dark fw-medium fs-16 mb-1">
                              Payment Mode
                            </p>
                            <p className="mb-0">{payment_mode}</p>
                          </div>
                          <div className="avatar bg-light d-flex align-items-center justify-content-center rounded">
                            <iconify-icon
                              icon="solar:clipboard-text-bold-duotone"
                              className="fs-35 text-primary"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-lg-4">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Order Summary</h4>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table mb-0">
                    <tbody>
                      <tr>
                        <td className="px-0">
                          <p className="d-flex mb-0 align-items-center gap-1">
                            <iconify-icon icon="solar:clipboard-text-broken" />
                            Sub Total :
                          </p>
                        </td>
                        <td className="text-end text-dark fw-medium px-0">
                          {formatCurrency(subtotal)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-0">
                          <p className="d-flex mb-0 align-items-center gap-1">
                            <iconify-icon
                              icon="solar:ticket-broken"
                              className="align-middle"
                            />{" "}
                            Discount :
                          </p>
                        </td>
                        <td className="text-end text-dark fw-medium px-0">
                          -{formatCurrency(discount)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-0">
                          <p className="d-flex mb-0 align-items-center gap-1">
                            <iconify-icon
                              icon="solar:kick-scooter-broken"
                              className="align-middle"
                            />{" "}
                            Delivery Charge :
                          </p>
                        </td>
                        <td className="text-end text-dark fw-medium px-0">
                          {formatCurrency(deliveryCharge)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-0">
                          <p className="d-flex mb-0 align-items-center gap-1">
                            <iconify-icon
                              icon="solar:calculator-minimalistic-broken"
                              className="align-middle"
                            />
                            Estimated Tax (15.5%) :
                          </p>
                        </td>
                        <td className="text-end text-dark fw-medium px-0">
                          {formatCurrency(tax)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card-footer d-flex align-items-center justify-content-between bg-light-subtle">
                <div>
                  <p className="fw-medium text-dark mb-0">Total Amount</p>
                </div>
                <div>
                  <p className="fw-medium text-dark mb-0">{formatCurrency(totalAmount)}</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Payment Information</h4>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="rounded-3 bg-light avatar d-flex align-items-center justify-content-center">
                    <img
                      src={payment_mode === 'Cash' ? '/images/card/cash.svg' : '/images/card/mastercard.svg'}
                      alt=""
                      className="avatar-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-dark fw-medium">{payment_mode}</p>
                    <p className="mb-0 text-dark">
                      {payment_mode === 'Cash' ? 'Cash on Delivery' : 'xxxx xxxx xxxx 7812'}
                    </p>
                  </div>
                  <div className="ms-auto">
                    <iconify-icon
                      icon="solar:check-circle-broken"
                      className={`fs-22 ${payment_status === 'Paid' ? 'text-success' : 'text-warning'}`}
                    />
                  </div>
                </div>
                <p className="text-dark mb-1 fw-medium">
                  Transaction ID :{" "}
                  <span className="text-muted fw-normal fs-13"> #{order_number}</span>
                </p>
                <p className="text-dark mb-0 fw-medium">
                  Card Holder Name :{" "}
                  <span className="text-muted fw-normal fs-13">
                    {" "}
                    {customer_name}
                  </span>
                </p>
              </div>
            </div>
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Customer Details</h4>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="/images/users/avatar-1.jpg"
                    alt=""
                    className="avatar rounded-3 border border-light border-3"
                  />
                  <div>
                    <p className="mb-1">{customer_name}</p>
                    <a href="#!" className="link-primary fw-medium">
                      Contact Customer
                    </a>
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-3">
                  <h5 className="">Contact Number</h5>
                  <div>
                    <a href="#!">
                      <i className="bx bx-edit-alt fs-18" />
                    </a>
                  </div>
                </div>
                <p className="mb-1">{phone}</p>
                <div className="d-flex justify-content-between mt-3">
                  <h5 className="">Shipping Address</h5>
                  <div>
                    <a href="#!">
                      <i className="bx bx-edit-alt fs-18" />
                    </a>
                  </div>
                </div>
                <div>
                  <p className="mb-1">{contact_address}</p>
                </div>
                <div className="d-flex justify-content-between mt-3">
                  <h5 className="">Billing Address</h5>
                  <div>
                    <a href="#!">
                      <i className="bx bx-edit-alt fs-18" />
                    </a>
                  </div>
                </div>
                <p className="mb-1">Same as shipping address</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="mapouter">
                  <div className="gmap_canvas">
                    <iframe
                      className="gmap_iframe rounded"
                      width="100%"
                      style={{ height: 418 }}
                      frameBorder={0}
                      scrolling="no"
                      marginHeight={0}
                      marginWidth={0}
                      src="https://maps.google.com/maps?width=1980&height=400&hl=en&q=Nagpur,+Maharashtra&t=&z=14&ie=UTF8&iwloc=B&output=embed"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};